import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import DressDisplay from './DressDisplay';
import ImageViewerModal from './ImageViewerModal';
import { getThumbnailUrl, getImageUrl } from '../../utils/api';
import { useLanguage } from '../../contexts/LanguageContext';
import { useSalon } from '../../contexts/SalonContext';
import './Salon.css';

// Helper function to generate real monthly appointments data
const generateMonthlyAppointmentsData = (appointments, year, getMonthName) => {
  const months = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map(i => getMonthName(i, true));
  
  // Initialize monthly counts
  const monthlyCounts = new Array(12).fill(0);
  
  // Count appointments by month using appointment_datetime
  appointments.forEach(appointment => {
    try {
      const appointmentDate = new Date(appointment.appointment_datetime || appointment.created_at);
      if (appointmentDate.getFullYear() === year) {
        const month = appointmentDate.getMonth();
        monthlyCounts[month]++;
      }
    } catch (error) {
      console.error('Error parsing appointment date:', error);
    }
  });
  
  return months.map((month, index) => ({
    month,
    value: monthlyCounts[index]
  }));
};

// Helper function to calculate monthly success rate (reservations/appointments percentage)
const generateMonthlySuccessRateData = (appointments, reservations, year, getMonthName) => {
  const months = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map(i => getMonthName(i, true));
  
  // Get monthly counts for both appointments and reservations
  const monthlyAppointmentCounts = new Array(12).fill(0);
  const monthlyReservationCounts = new Array(12).fill(0);
  
  // Count appointments by month
  appointments.forEach(appointment => {
    try {
      const appointmentDate = new Date(appointment.appointment_datetime || appointment.created_at);
      if (appointmentDate.getFullYear() === year) {
        const month = appointmentDate.getMonth();
        monthlyAppointmentCounts[month]++;
      }
    } catch (error) {
      console.error('Error parsing appointment date:', error);
    }
  });
  
  // Count reservations by month
  reservations.forEach(reservation => {
    try {
      const reservationDate = new Date(reservation.reservation_date || reservation.created_at);
      if (reservationDate.getFullYear() === year) {
        const month = reservationDate.getMonth();
        monthlyReservationCounts[month]++;
      }
    } catch (error) {
      console.error('Error parsing reservation date:', error);
    }
  });
  
  // Calculate success rate for each month
  return months.map((month, index) => {
    const appointments = monthlyAppointmentCounts[index];
    const reservations = monthlyReservationCounts[index];
    const successRate = appointments > 0 ? Math.round((reservations / appointments) * 100) : 0;
    
    return {
      month,
      value: successRate,
      appointments: appointments,
      reservations: reservations
    };
  });
};

// Per-employee conversion: linked reservation_id / assigned appointments (by appointment_datetime month)
const generateEmployeeConversionStats = (appointments, members, year, getMonthName) => {
  const allowedMembers = new Map();
  (members || []).forEach(m => {
    if (m.role === 'admin' || m.role === 'member') {
      allowedMembers.set(m.id, m);
    }
  });

  const byEmployee = new Map();

  appointments.forEach(appointment => {
    const employeeId = appointment.employee_id;
    if (!employeeId || !allowedMembers.has(employeeId)) return;

    try {
      const appointmentDate = new Date(appointment.appointment_datetime || appointment.created_at);
      if (appointmentDate.getFullYear() !== year) return;

      const month = appointmentDate.getMonth();
      if (!byEmployee.has(employeeId)) {
        byEmployee.set(employeeId, {
          monthlyTotal: new Array(12).fill(0),
          monthlyLinked: new Array(12).fill(0)
        });
      }

      const stats = byEmployee.get(employeeId);
      stats.monthlyTotal[month]++;
      if (appointment.reservation_id) {
        stats.monthlyLinked[month]++;
      }
    } catch (error) {
      console.error('Error parsing appointment date:', error);
    }
  });

  const months = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map(i => getMonthName(i, true));
  const result = [];

  byEmployee.forEach((stats, employeeId) => {
    const member = allowedMembers.get(employeeId);
    const yearAppointments = stats.monthlyTotal.reduce((sum, n) => sum + n, 0);
    if (yearAppointments === 0) return;

    const yearLinked = stats.monthlyLinked.reduce((sum, n) => sum + n, 0);
    const name = `${member.first_name || ''} ${member.last_name || ''}`.trim();

    const monthly = months.map((month, index) => {
      const total = stats.monthlyTotal[index];
      const linked = stats.monthlyLinked[index];
      const value = total > 0 ? Math.round((linked / total) * 100) : 0;
      return { month, value, appointments: total, linked };
    });

    result.push({
      employeeId,
      name: name || String(employeeId),
      role: member.role,
      yearTotal: {
        appointments: yearAppointments,
        linked: yearLinked,
        rate: Math.round((yearLinked / yearAppointments) * 100)
      },
      monthly
    });
  });

  result.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  return result;
};

// Helper function to generate real monthly reservations data
const generateMonthlyReservationsData = (reservations, year, getMonthName) => {
  const months = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map(i => getMonthName(i, true));
  
  // Initialize monthly counts
  const monthlyCounts = new Array(12).fill(0);
  
  // Count reservations by month using reservation_date
  reservations.forEach(reservation => {
    try {
      const reservationDate = new Date(reservation.reservation_date || reservation.created_at);
      if (reservationDate.getFullYear() === year) {
        const month = reservationDate.getMonth();
        monthlyCounts[month]++;
      }
    } catch (error) {
      console.error('Error parsing reservation date:', error);
    }
  });
  
  return months.map((month, index) => ({
    month,
    value: monthlyCounts[index]
  }));
};

// Helper function to generate real monthly revenue data
const generateMonthlyRevenueData = (reservations, year, getMonthName) => {
  const months = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map(i => getMonthName(i, true));
  
  // Initialize monthly revenue
  const monthlyRevenue = new Array(12).fill(0);
  
  // Sum revenue by month: deposit on reservation_date + (price - deposit) on wedding_date
  reservations.forEach(reservation => {
    try {
      const price = parseFloat(reservation.price) || 0;
      const deposit = parseFloat(reservation.deposit) || 0;
      const remaining = price - deposit;
      
      const reservationDate = new Date(reservation.reservation_date || reservation.created_at);
      const weddingDate = new Date(reservation.wedding_date);
      
      // Add deposit if reservation was made in selected year
      if (reservationDate.getFullYear() === year) {
        const month = reservationDate.getMonth();
        monthlyRevenue[month] += deposit;
      }
      
      // Add remaining amount if wedding was in selected year
      if (weddingDate.getFullYear() === year) {
        const month = weddingDate.getMonth();
        monthlyRevenue[month] += remaining;
      }
    } catch (error) {
      console.error('Error parsing reservation dates or price:', error);
    }
  });
  
  return months.map((month, index) => ({
    month,
    value: Math.round(monthlyRevenue[index])
  }));
};

// Helper function to generate monthly average reservation price (by reservation_date, price field)
const generateMonthlyAverageReservationPriceData = (reservations, year, getMonthName) => {
  const months = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map(i => getMonthName(i, true));
  const monthlySum = new Array(12).fill(0);
  const monthlyCount = new Array(12).fill(0);

  reservations.forEach(reservation => {
    try {
      const reservationDate = new Date(reservation.reservation_date || reservation.created_at);
      if (reservationDate.getFullYear() === year) {
        const month = reservationDate.getMonth();
        const price = parseFloat(reservation.price) || 0;
        monthlySum[month] += price;
        monthlyCount[month]++;
      }
    } catch (error) {
      console.error('Error parsing reservation date:', error);
    }
  });

  return months.map((month, index) => ({
    month,
    value: monthlyCount[index] > 0 ? Math.round(monthlySum[index] / monthlyCount[index]) : 0
  }));
};

// Helper function to calculate monthly weddings (based on wedding_date from reservations)
const generateMonthlyWeddingsData = (reservations, year, getMonthName) => {
  const months = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map(i => getMonthName(i, true));
  
  // Initialize monthly wedding counts
  const monthlyWeddings = new Array(12).fill(0);
  
  // Count weddings by month using wedding_date
  reservations.forEach(reservation => {
    try {
      const weddingDate = new Date(reservation.wedding_date || reservation.created_at);
      if (weddingDate.getFullYear() === year) {
        const month = weddingDate.getMonth();
        monthlyWeddings[month]++;
      }
    } catch (error) {
      console.error('Error parsing wedding date:', error);
    }
  });
  
  return months.map((month, index) => ({
    month,
    value: monthlyWeddings[index]
  }));
};

const percentile = (sortedArr, p) => {
  if (!sortedArr.length) return null;
  if (sortedArr.length === 1) return sortedArr[0];
  const index = p * (sortedArr.length - 1);
  const lower = Math.floor(index);
  const upper = Math.ceil(index);
  if (lower === upper) return sortedArr[lower];
  const frac = index - lower;
  return sortedArr[lower] + frac * (sortedArr[upper] - sortedArr[lower]);
};

const generateBrideAgeHistogram = (reservations, year = null) => {
  const filteredReservations = year ? reservations.filter(reservation => {
    try {
      const reservationDate = new Date(reservation.reservation_date || reservation.created_at);
      return reservationDate.getFullYear() === year;
    } catch (error) {
      return false;
    }
  }) : reservations;

  const ageCounts = {};
  for (let age = 18; age <= 40; age++) {
    ageCounts[age] = 0;
  }
  const agesList = [];
  let totalDataPoints = 0;

  filteredReservations.forEach(reservation => {
    const birthYear = reservation.bride_birth_year != null && reservation.bride_birth_year !== ''
      ? parseInt(reservation.bride_birth_year, 10)
      : NaN;
    if (Number.isNaN(birthYear)) return;
    let reservationDate;
    try {
      reservationDate = new Date(reservation.reservation_date || reservation.created_at);
      if (Number.isNaN(reservationDate.getTime())) return;
    } catch (error) {
      return;
    }
    const age = reservationDate.getFullYear() - birthYear;
    if (age >= 18 && age <= 40) {
      ageCounts[age]++;
      agesList.push(age);
      totalDataPoints++;
    }
  });

  const data = Array.from({ length: 23 }, (_, i) => ({
    age: 18 + i,
    value: ageCounts[18 + i]
  }));

  let averageAge = null;
  let medianAge = null;
  let range90 = null;
  let range95 = null;

  if (agesList.length > 0) {
    const sortedAges = [...agesList].sort((a, b) => a - b);
    const sum = agesList.reduce((s, x) => s + x, 0);
    averageAge = Math.round((sum / agesList.length) * 10) / 10;
    medianAge = Math.round(percentile(sortedAges, 0.5));
    range90 = [
      Math.round(percentile(sortedAges, 0.05)),
      Math.round(percentile(sortedAges, 0.95))
    ];
    range95 = [
      Math.round(percentile(sortedAges, 0.025)),
      Math.round(percentile(sortedAges, 0.975))
    ];
  }

  return { data, totalDataPoints, averageAge, medianAge, range90, range95 };
};

const ManageAnalytics = ({ salonId, userRole }) => {
  const { t, getMonthName } = useLanguage();
  const { currencySymbol, formatPrice } = useSalon();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [popularDressesYear, setPopularDressesYear] = useState('current'); // 'current' or 'total'
  const [popularDressesSortBy, setPopularDressesSortBy] = useState('reservations'); // 'reservations' | 'revenue' | 'interests'
  const [popularBrandsYear, setPopularBrandsYear] = useState('current'); // 'current' or 'total'
  const [popularReferralSourcesYear, setPopularReferralSourcesYear] = useState('current'); // 'current' or 'total'
  const [brideCitiesYear, setBrideCitiesYear] = useState('current'); // 'current' or 'total'
  const [brideAgeYear, setBrideAgeYear] = useState('current'); // 'current' or 'total'
  const [showYearlyOverview, setShowYearlyOverview] = useState(false);
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [selectedDressImages, setSelectedDressImages] = useState([]);
  const [analyticsData, setAnalyticsData] = useState({
    totalReservations: 0,
    totalAppointments: 0,
    totalWeddings: 0,
    successRate: 0,
    totalDresses: 0,
    totalRevenue: 0,
    averageReservationPrice: 0,
    monthlyAppointments: [],
    monthlyReservations: [],
    monthlyWeddings: [],
    monthlySuccessRate: [],
    monthlyRevenue: [],
    monthlyAverageReservationPrice: [],
    prevMonthlyAppointments: [],
    prevMonthlyReservations: [],
    prevMonthlyWeddings: [],
    prevMonthlySuccessRate: [],
    prevMonthlyRevenue: [],
    prevMonthlyAverageReservationPrice: [],
    yearlyData: [],
    popularDresses: [],
    popularBrands: [],
    popularReferralSources: [],
    brideCities: [],
    brideAgeHistogram: {
      data: Array.from({ length: 23 }, (_, i) => ({ age: 18 + i, value: 0 })),
      totalDataPoints: 0,
      averageAge: null,
      medianAge: null,
      range90: null,
      range95: null
    },
    employeeConversionStats: [],
    recentActivity: []
  });

  const fetchAnalyticsData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Fetch full data for all metrics and filtering
      console.log('Fetching analytics data...');
      
      const fullReservationsRes = await axios.get(`/api/salons/${salonId}/reservations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Reservations fetched successfully');
      
      const fullAppointmentsRes = await axios.get(`/api/salons/${salonId}/appointments?appointments_only=true`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Appointments fetched successfully');
      
      const fullDressesRes = await axios.get(`/api/salons/${salonId}/dresses?limit=1000000`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Dresses fetched successfully');
      
      const fullBrandsRes = await axios.get(`/api/salons/${salonId}/dress-brands?limit=1000000`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Brands fetched successfully');

      const membersRes = await axios.get(`/api/salons/${salonId}/members`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Members fetched successfully');

      // Get all-time totals for dresses
      const totalDresses = fullDressesRes.data.pagination?.totalItems || 0;

      // Filter reservations and appointments by selected year for Key Metrics
      const reservationsForYear = fullReservationsRes.data.reservations?.filter(reservation => {
        try {
          const reservationDate = new Date(reservation.reservation_date || reservation.created_at);
          return reservationDate.getFullYear() === selectedYear;
        } catch (error) {
          return false;
        }
      }) || [];

      // Now we get only actual appointments from appointments table (no calendar events)
      const actualAppointments = fullAppointmentsRes.data.appointments || [];

      const appointmentsForYear = actualAppointments.filter(appointment => {
        try {
          const appointmentDate = new Date(appointment.appointment_datetime || appointment.created_at);
          return appointmentDate.getFullYear() === selectedYear;
        } catch (error) {
          return false;
        }
      });

      // Calculate year-specific totals for Key Metrics
      const totalReservations = reservationsForYear.length;
      const totalAppointments = appointmentsForYear.length;
      
      // Calculate revenue: deposit on reservation_date + (price - deposit) on wedding_date
      // IMPORTANT: Use ALL reservations (not just reservationsForYear) to match monthly revenue calculation
      // This ensures we capture revenue from weddings in the selected year even if reservation was made in a different year
      const totalRevenue = (fullReservationsRes.data.reservations || []).reduce((sum, reservation) => {
        const price = parseFloat(reservation.price) || 0;
        const deposit = parseFloat(reservation.deposit) || 0;
        const remaining = price - deposit;
        
        try {
          const reservationDate = new Date(reservation.reservation_date || reservation.created_at);
          const weddingDate = new Date(reservation.wedding_date);
          
          let revenue = 0;
          
          // Add deposit if reservation was made in selected year
          if (reservationDate.getFullYear() === selectedYear) {
            revenue += deposit;
          }
          
          // Add remaining amount if wedding was in selected year
          if (weddingDate.getFullYear() === selectedYear) {
            revenue += remaining;
          }
          
          return sum + revenue;
        } catch (error) {
          console.error('Error parsing reservation dates:', error);
          return sum;
        }
      }, 0);

      // Calculate total weddings for the year (based on wedding_date from reservations)
      // IMPORTANT: Use ALL reservations (not just reservationsForYear) to match monthly weddings calculation
      // This ensures we capture weddings in the selected year even if reservation was made in a different year
      const totalWeddings = (fullReservationsRes.data.reservations || []).filter(reservation => {
        try {
          const weddingDate = new Date(reservation.wedding_date || reservation.created_at);
          return weddingDate.getFullYear() === selectedYear;
        } catch (error) {
          return false;
        }
      }).length;

      // Calculate success rate for the year (reservations/appointments percentage)
      const successRate = totalAppointments > 0 ? Math.round((totalReservations / totalAppointments) * 100) : 0;

      // Average reservation price for the year (by reservation_date, price field)
      const averageReservationPrice = reservationsForYear.length > 0
        ? Math.round(
            reservationsForYear.reduce((sum, r) => sum + (parseFloat(r.price) || 0), 0) / reservationsForYear.length
          )
        : 0;

      // Generate real monthly data from actual data (use only real appointments, not calendar events)
      const monthlyAppointments = generateMonthlyAppointmentsData(actualAppointments, selectedYear, getMonthName);
      const monthlyReservations = generateMonthlyReservationsData(fullReservationsRes.data.reservations || [], selectedYear, getMonthName);
      const monthlyWeddings = generateMonthlyWeddingsData(fullReservationsRes.data.reservations || [], selectedYear, getMonthName);
      const monthlySuccessRate = generateMonthlySuccessRateData(actualAppointments, fullReservationsRes.data.reservations || [], selectedYear, getMonthName);
      const monthlyRevenue = generateMonthlyRevenueData(fullReservationsRes.data.reservations || [], selectedYear, getMonthName);
      const monthlyAverageReservationPrice = generateMonthlyAverageReservationPriceData(fullReservationsRes.data.reservations || [], selectedYear, getMonthName);

      const prevYear = selectedYear - 1;
      const prevMonthlyAppointments = generateMonthlyAppointmentsData(actualAppointments, prevYear, getMonthName);
      const prevMonthlyReservations = generateMonthlyReservationsData(fullReservationsRes.data.reservations || [], prevYear, getMonthName);
      const prevMonthlyWeddings = generateMonthlyWeddingsData(fullReservationsRes.data.reservations || [], prevYear, getMonthName);
      const prevMonthlySuccessRate = generateMonthlySuccessRateData(actualAppointments, fullReservationsRes.data.reservations || [], prevYear, getMonthName);
      const prevMonthlyRevenue = generateMonthlyRevenueData(fullReservationsRes.data.reservations || [], prevYear, getMonthName);
      const prevMonthlyAverageReservationPrice = generateMonthlyAverageReservationPriceData(fullReservationsRes.data.reservations || [], prevYear, getMonthName);

      const allReservations = fullReservationsRes.data.reservations || [];
      const thisYear = new Date().getFullYear();
      const allYears = new Set();
      allReservations.forEach(r => {
        [r.reservation_date, r.wedding_date, r.created_at].forEach(d => {
          if (d) { const y = new Date(d).getFullYear(); if (y > 1900) allYears.add(y); }
        });
      });
      actualAppointments.forEach(a => {
        const d = a.appointment_datetime || a.created_at;
        if (d) { const y = new Date(d).getFullYear(); if (y > 1900) allYears.add(y); }
      });
      const earliestYear = allYears.size > 0 ? Math.min(...allYears) : thisYear;
      const startYear = Math.max(earliestYear, thisYear - 14);
      const yearRange = Array.from({ length: thisYear - startYear + 1 }, (_, i) => startYear + i);

      const yearlyData = yearRange.map(year => {
        const mApp = generateMonthlyAppointmentsData(actualAppointments, year, getMonthName);
        const mRes = generateMonthlyReservationsData(allReservations, year, getMonthName);
        const mWed = generateMonthlyWeddingsData(allReservations, year, getMonthName);
        const mRev = generateMonthlyRevenueData(allReservations, year, getMonthName);
        const mAvg = generateMonthlyAverageReservationPriceData(allReservations, year, getMonthName);

        const totalApp = mApp.reduce((s, m) => s + m.value, 0);
        const totalRes = mRes.reduce((s, m) => s + m.value, 0);
        const totalWed = mWed.reduce((s, m) => s + m.value, 0);
        const totalRev = mRev.reduce((s, m) => s + m.value, 0);
        const sr = totalApp > 0 ? Math.round((totalRes / totalApp) * 100) : 0;
        const priceMonths = mAvg.filter(m => m.value > 0);
        const avgP = priceMonths.length > 0 ? Math.round(priceMonths.reduce((s, m) => s + m.value, 0) / priceMonths.length) : 0;

        return { year, weddings: totalWed, revenue: totalRev, appointments: totalApp, reservations: totalRes, successRate: sr, avgPrice: avgP };
      });

      const popularDressesParams = new URLSearchParams();
      if (popularDressesYear === 'current') popularDressesParams.set('year', selectedYear);
      popularDressesParams.set('sortBy', popularDressesSortBy === 'interests' ? 'interested' : popularDressesSortBy);
      const popularDressesRes = await axios.get(
        `/api/salons/${salonId}/analytics/popular-dresses?${popularDressesParams.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      ).catch(() => ({ data: { list: [] } }));
      const dressList = fullDressesRes.data.dresses || [];
      const popularDressesList = popularDressesRes.data?.list || [];
      const popularDresses = popularDressesList.map(({ dressId, reservations, revenue, interestedCount }) => {
        const dress = dressList.find(d => d.id === dressId);
        if (!dress) return null;
        return {
          ...dress,
          reservations: reservations ?? 0,
          revenue: revenue ?? 0,
          interestedCount: interestedCount ?? 0
        };
      }).filter(Boolean);
      const popularBrands = generatePopularBrands(
        fullBrandsRes.data.brands || [],
        fullDressesRes.data.dresses || [], 
        fullReservationsRes.data.reservations || [], 
        popularBrandsYear === 'current' ? selectedYear : null
      );
      const popularReferralSources = generatePopularReferralSources(
        fullReservationsRes.data.reservations || [],
        popularReferralSourcesYear === 'current' ? selectedYear : null
      );
      const brideCities = generateBrideCityStats(
        fullReservationsRes.data.reservations || [],
        brideCitiesYear === 'current' ? selectedYear : null
      );
      const brideAgeHistogram = generateBrideAgeHistogram(
        fullReservationsRes.data.reservations || [],
        brideAgeYear === 'current' ? selectedYear : null
      );
      const recentActivity = generateRecentActivity(fullReservationsRes.data.reservations || [], actualAppointments, t);
      const employeeConversionStats = generateEmployeeConversionStats(
        actualAppointments,
        membersRes.data.members || [],
        selectedYear,
        getMonthName
      );

      setAnalyticsData({
        totalReservations,
        totalAppointments,
        totalWeddings,
        successRate,
        totalDresses,
        totalRevenue,
        averageReservationPrice,
        monthlyAppointments,
        monthlyReservations,
        monthlyWeddings,
        monthlySuccessRate,
        monthlyRevenue,
        monthlyAverageReservationPrice,
        prevMonthlyAppointments,
        prevMonthlyReservations,
        prevMonthlyWeddings,
        prevMonthlySuccessRate,
        prevMonthlyRevenue,
        prevMonthlyAverageReservationPrice,
        yearlyData,
        popularDresses,
        popularBrands,
        popularReferralSources,
        brideCities,
        brideAgeHistogram,
        employeeConversionStats,
        recentActivity
      });

    } catch (error) {
      console.error('Error fetching analytics data:', error);
      console.error('Error details:', error.response?.data || error.message);
      setError(`Failed to fetch analytics data: ${error.response?.data?.error || error.message}`);
    } finally {
      setLoading(false);
    }
  }, [salonId, selectedYear, popularDressesYear, popularDressesSortBy, popularBrandsYear, popularReferralSourcesYear, brideCitiesYear, brideAgeYear, getMonthName, t]);

  // Year navigation functions
  const handlePreviousYear = () => {
    setSelectedYear(prev => prev - 1);
  };

  const handleNextYear = () => {
    setSelectedYear(prev => prev + 1);
  };

  const handleCurrentYear = () => {
    setSelectedYear(new Date().getFullYear());
  };

  const handleImageClick = (dress) => {
    if (dress.images && dress.images.length > 0) {
      // Convert relative paths to full URLs
      const fullImageUrls = dress.images.map(imagePath => getImageUrl(imagePath));
      
      // Add video as the last item if it exists
      if (dress.video_url) {
        const videoUrl = getImageUrl(dress.video_url);
        fullImageUrls.push({
          type: 'video',
          url: videoUrl,
          thumbnail: videoUrl
        });
      }
      
      // Add second video if it exists
      if (dress.video2_url) {
        const video2Url = getImageUrl(dress.video2_url);
        fullImageUrls.push({
          type: 'video',
          url: video2Url,
          thumbnail: video2Url
        });
      }
      
      setSelectedDressImages(fullImageUrls);
      setImageViewerOpen(true);
    }
  };

  const handleCloseImageViewer = () => {
    setImageViewerOpen(false);
    setSelectedDressImages([]);
  };

  const generatePopularBrands = (brands, dresses, reservations, year = null) => {
    // Filter reservations by year if specified
    const filteredReservations = year ? reservations.filter(reservation => {
      try {
        const reservationDate = new Date(reservation.reservation_date || reservation.created_at);
        return reservationDate.getFullYear() === year;
      } catch (error) {
        return false;
      }
    }) : reservations;
    
    // Calculate actual reservations count and revenue for each brand
    const brandStats = brands.map(brand => {
      // Get all dresses for this brand
      const brandDresses = dresses.filter(dress => dress.brand_id === brand.id);
      
      // Get all reservations for dresses of this brand
      const brandReservations = filteredReservations.filter(reservation => 
        brandDresses.some(dress => dress.id === reservation.dress_id)
      );
      
      const reservationsCount = brandReservations.length;
      const dressesCount = brandDresses.length;
      const reservationsPerDress = dressesCount > 0 ? Math.round((reservationsCount / dressesCount) * 10) / 10 : 0; // Round to 1 decimal place
      const revenue = brandReservations.reduce((sum, reservation) => {
        const price = parseFloat(reservation.price) || 0;
        const deposit = parseFloat(reservation.deposit) || 0;
        const remaining = price - deposit;
        
        try {
          const reservationDate = new Date(reservation.reservation_date || reservation.created_at);
          const weddingDate = new Date(reservation.wedding_date);
          
          let revenue = 0;
          
          // Add deposit if reservation was made in selected year (or all years if year is null)
          if (!year || reservationDate.getFullYear() === year) {
            revenue += deposit;
          }
          
          // Add remaining amount if wedding was in selected year (or all years if year is null)
          if (!year || weddingDate.getFullYear() === year) {
            revenue += remaining;
          }
          
          return sum + revenue;
        } catch (error) {
          console.error('Error parsing reservation dates:', error);
          return sum;
        }
      }, 0);
      
      return {
        id: brand.id,
        name: brand.brand_name,
        country: brand.country || 'Unknown',
        image: brand.display_image || null,
        reservations: reservationsCount,
        dresses: dressesCount,
        reservationsPerDress: reservationsPerDress,
        revenue: Math.round(revenue)
      };
    });
    
    // Sort by reservations count (descending) and take top 10
    return brandStats
      .sort((a, b) => b.reservations - a.reservations)
      .slice(0, 10);
  };

  // Helper function to generate popular referral sources
  const generatePopularReferralSources = (reservations, year = null) => {
    // Filter reservations by year if specified
    const filteredReservations = year ? reservations.filter(reservation => {
      try {
        const reservationDate = new Date(reservation.reservation_date || reservation.created_at);
        return reservationDate.getFullYear() === year;
      } catch (error) {
        return false;
      }
    }) : reservations;
    
    // Collect all referral sources from reservations
    const sourceStats = {};
    
    filteredReservations.forEach(reservation => {
      if (reservation.referral_sources && Array.isArray(reservation.referral_sources)) {
        reservation.referral_sources.forEach(source => {
          const sourceName = source.source_name;
          
          if (!sourceStats[sourceName]) {
            sourceStats[sourceName] = {
              name: sourceName,
              reservations: 0,
              revenue: 0
            };
          }
          
          sourceStats[sourceName].reservations++;
          
          // Calculate revenue for this reservation
          const price = parseFloat(reservation.price) || 0;
          const deposit = parseFloat(reservation.deposit) || 0;
          const remaining = price - deposit;
          
          try {
            const reservationDate = new Date(reservation.reservation_date || reservation.created_at);
            const weddingDate = new Date(reservation.wedding_date);
            
            let revenue = 0;
            
            // Add deposit if reservation was made in selected year (or all years if year is null)
            if (!year || reservationDate.getFullYear() === year) {
              revenue += deposit;
            }
            
            // Add remaining amount if wedding was in selected year (or all years if year is null)
            if (!year || weddingDate.getFullYear() === year) {
              revenue += remaining;
            }
            
            sourceStats[sourceName].revenue += revenue;
          } catch (error) {
            console.error('Error parsing reservation dates:', error);
          }
        });
      }
    });
    
    // Convert to array and round revenue
    const sourcesArray = Object.values(sourceStats).map(source => ({
      ...source,
      revenue: Math.round(source.revenue)
    }));
    
    // Sort by reservations count (descending) and take top 10
    return sourcesArray
      .sort((a, b) => b.reservations - a.reservations)
      .slice(0, 10);
  };

  const generateBrideCityStats = (reservations, year = null) => {
    const filteredReservations = year ? reservations.filter(reservation => {
      try {
        const reservationDate = new Date(reservation.reservation_date || reservation.created_at);
        return reservationDate.getFullYear() === year;
      } catch (error) {
        return false;
      }
    }) : reservations;

    const cityCounts = {};

    filteredReservations.forEach(reservation => {
      const cityName = (reservation.bride_city || '').trim();
      if (!cityName) {
        return;
      }

      cityCounts[cityName] = (cityCounts[cityName] || 0) + 1;
    });

    const totalCount = Object.values(cityCounts).reduce((sum, count) => sum + count, 0);

    return Object.entries(cityCounts)
      .map(([name, count]) => ({
        name,
        count,
        percentage: totalCount > 0 ? Math.round((count / totalCount) * 1000) / 10 : 0
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  };

  // Helper function to generate recent activity
  const generateRecentActivity = (reservations, appointments, t) => {
    const activities = [];
    
    // Add recent reservations
    reservations.slice(0, 3).forEach(reservation => {
      activities.push({
        type: 'reservation',
        title: `${t('analytics.newReservationBy')} ${reservation.bride_name} ${reservation.bride_surname}`,
        date: new Date(reservation.created_at),
        amount: parseFloat(reservation.price) || 0
      });
    });

    // Add recent appointments
    appointments.slice(0, 3).forEach(appointment => {
      activities.push({
        type: 'appointment',
        title: `${t('analytics.appointmentWith')} ${appointment.name} ${appointment.surname}`,
        date: new Date(appointment.appointment_datetime),
        amount: 0
      });
    });

    // Sort by date and return recent 5
    return activities
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 5);
  };

  useEffect(() => {
    fetchAnalyticsData();
  }, [fetchAnalyticsData]);

  if (loading) {
    return (
      <div className="manage-workspace">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>{t('analytics.loadingAnalytics')}</p>
        </div>
      </div>
    );
  }

  const YearlyBarChart = ({ data, colorClass, formatValue, suffix = '' }) => {
    const maxVal = Math.max(...data.map(d => d.value), 1);
    const fmt = formatValue || (v => v.toLocaleString());
    return (
      <div className="chart-placeholder">
        <div className="chart-bars yearly-chart-bars">
          {data.map((d, i) => (
            <div key={i} className="chart-bar">
              <div className="bar-value">{fmt(d.value)}{suffix}</div>
              <div className="bar-area">
                <div className={`bar-fill ${colorClass}`} style={{ height: `${(d.value / maxVal) * 100}%` }}></div>
              </div>
              <div className="bar-label">{d.year}</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="">
      <div className="table-header">
        <h2>{t('analytics.title')}</h2>
        <div className="header-actions">
          <div className="year-selector">
            <button 
              className="year-nav-btn" 
              onClick={handlePreviousYear}
              title={t('analytics.previousYear')}
            >
              ‹
            </button>
            <span 
              className="current-year"
              onClick={handleCurrentYear}
              title={t('analytics.currentYear')}
            >
              {selectedYear}
            </span>
            <button 
              className="year-nav-btn" 
              onClick={handleNextYear}
              title={t('analytics.nextYear')}
            >
              ›
          </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="analytics-content">
        {/* Key Metrics */}
        <div className="analytics-section">
          <h3>{t('analytics.keyMetrics')}</h3>
          <div className="metrics-grid">
            <div className="metric-card">
              <div className="metric-icon">💒</div>
              <div className="metric-content">
                <div className="metric-value">{analyticsData.totalWeddings}</div>
                <div className="metric-label">{t('analytics.weddings')} ({selectedYear})</div>
              </div>
            </div>
            <div className="metric-card revenue">
              <div className="metric-icon">💰</div>
              <div className="metric-content">
                <div className="metric-value">{formatPrice(analyticsData.totalRevenue)}</div>
                <div className="metric-label">{t('analytics.revenue')} ({selectedYear})</div>
              </div>
            </div>
            <div className="metric-card">
              <div className="metric-icon">📅</div>
              <div className="metric-content">
                <div className="metric-value">{analyticsData.totalAppointments}</div>
                <div className="metric-label">{t('analytics.appointments')} ({selectedYear})</div>
              </div>
            </div>
            <div className="metric-card">
              <div className="metric-icon">📋</div>
              <div className="metric-content">
                <div className="metric-value">{analyticsData.totalReservations}</div>
                <div className="metric-label">{t('analytics.reservations')} ({selectedYear})</div>
              </div>
            </div>
            <div className="metric-card">
              <div className="metric-icon">🏷️</div>
              <div className="metric-content">
                <div className="metric-value">{formatPrice(analyticsData.averageReservationPrice)}</div>
                <div className="metric-label">{t('analytics.reservationPrice')} ({selectedYear})</div>
              </div>
            </div>
            <div className="metric-card">
              <div className="metric-icon">📈</div>
              <div className="metric-content">
                <div className="metric-value">{analyticsData.successRate}%</div>
                <div className="metric-label">{t('analytics.successRate')} ({selectedYear})</div>
              </div>
            </div>
            <div className="metric-card">
              <div className="metric-icon">👗</div>
              <div className="metric-content">
                <div className="metric-value">{analyticsData.totalDresses}</div>
                <div className="metric-label">{t('analytics.totalDresses')}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Charts Section */}
        {showYearlyOverview ? (
        <div className="analytics-section">
          <div className="section-header">
            <h3>{t('analytics.yearlyOverview')}</h3>
            <button
              className="year-nav-btn yearly-toggle active"
              onClick={() => setShowYearlyOverview(false)}
            >
              {t('analytics.monthlyOverview')}
            </button>
          </div>
          <div className="charts-grid">
            <div className="chart-card">
              <h4>{t('analytics.weddings')}</h4>
              <YearlyBarChart data={analyticsData.yearlyData.map(d => ({ year: d.year, value: d.weddings }))} colorClass="weddings" />
            </div>
            <div className="chart-card">
              <h4>{t('analytics.revenue')} ({currencySymbol})</h4>
              <YearlyBarChart data={analyticsData.yearlyData.map(d => ({ year: d.year, value: d.revenue }))} colorClass="revenue" />
            </div>
            <div className="chart-card">
              <h4>{t('analytics.appointments')}</h4>
              <YearlyBarChart data={analyticsData.yearlyData.map(d => ({ year: d.year, value: d.appointments }))} colorClass="appointments" />
            </div>
            <div className="chart-card">
              <h4>{t('analytics.reservations')}</h4>
              <YearlyBarChart data={analyticsData.yearlyData.map(d => ({ year: d.year, value: d.reservations }))} colorClass="" />
            </div>
            <div className="chart-card">
              <h4>{t('analytics.successRate')}</h4>
              <YearlyBarChart data={analyticsData.yearlyData.map(d => ({ year: d.year, value: d.successRate }))} colorClass="success-rate" suffix="%" />
            </div>
            <div className="chart-card">
              <h4>{t('analytics.reservationPrice')} ({currencySymbol})</h4>
              <YearlyBarChart data={analyticsData.yearlyData.map(d => ({ year: d.year, value: d.avgPrice }))} colorClass="reservation-price" />
            </div>
          </div>
        </div>
        ) : (
        <div className="analytics-section">
          <div className="section-header">
            <h3>{t('analytics.monthlyOverview')}</h3>
            <button
              className="year-nav-btn yearly-toggle"
              onClick={() => setShowYearlyOverview(true)}
            >
              {t('analytics.yearlyStatistics')}
            </button>
          </div>
          <div className="charts-grid">
            <div className="chart-card">
              <h4>{t('analytics.weddingsPerMonth')}</h4>
              <div className="chart-legend">
                <div className="chart-legend-item"><div className="chart-legend-swatch bar-fill weddings prev-year"></div><span>{selectedYear - 1}</span></div>
                <div className="chart-legend-item"><div className="chart-legend-swatch bar-fill weddings"></div><span>{selectedYear}</span></div>
              </div>
              <div className="chart-placeholder">
                <div className="chart-bars">
                  {(() => {
                    const max = Math.max(...analyticsData.monthlyWeddings.map(m => m.value), ...analyticsData.prevMonthlyWeddings.map(m => m.value), 1);
                    return analyticsData.monthlyWeddings.map((item, index) => {
                      const prevValue = analyticsData.prevMonthlyWeddings[index]?.value || 0;
                      return (
                        <div key={index} className="chart-bar">
                          <div className="bar-value">{item.value}</div>
                          <div className="bar-area">
                            <div className="bar-fill weddings prev-year" style={{ height: `${(prevValue / max) * 100}%` }} title={`${selectedYear - 1}: ${prevValue}`}></div>
                            <div className="bar-fill weddings" style={{ height: `${(item.value / max) * 100}%` }}></div>
                          </div>
                          <div className="bar-label">{item.month}</div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            </div>
            <div className="chart-card">
              <h4>{t('analytics.revenuePerMonth')} ({currencySymbol})</h4>
              <div className="chart-legend">
                <div className="chart-legend-item"><div className="chart-legend-swatch bar-fill revenue prev-year"></div><span>{selectedYear - 1}</span></div>
                <div className="chart-legend-item"><div className="chart-legend-swatch bar-fill revenue"></div><span>{selectedYear}</span></div>
              </div>
              <div className="chart-placeholder">
                <div className="chart-bars">
                  {(() => {
                    const max = Math.max(...analyticsData.monthlyRevenue.map(m => m.value), ...analyticsData.prevMonthlyRevenue.map(m => m.value), 1);
                    return analyticsData.monthlyRevenue.map((item, index) => {
                      const prevValue = analyticsData.prevMonthlyRevenue[index]?.value || 0;
                      return (
                        <div key={index} className="chart-bar">
                          <div className="bar-value">{item.value.toLocaleString()}</div>
                          <div className="bar-area">
                            <div className="bar-fill revenue prev-year" style={{ height: `${(prevValue / max) * 100}%` }} title={`${selectedYear - 1}: ${prevValue.toLocaleString()}`}></div>
                            <div className="bar-fill revenue" style={{ height: `${(item.value / max) * 100}%` }}></div>
                          </div>
                          <div className="bar-label">{item.month}</div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            </div>
            <div className="chart-card">
              <h4>{t('analytics.appointmentsPerMonth')}</h4>
              <div className="chart-legend">
                <div className="chart-legend-item"><div className="chart-legend-swatch bar-fill appointments prev-year"></div><span>{selectedYear - 1}</span></div>
                <div className="chart-legend-item"><div className="chart-legend-swatch bar-fill appointments"></div><span>{selectedYear}</span></div>
              </div>
              <div className="chart-placeholder">
                <div className="chart-bars">
                  {(() => {
                    const max = Math.max(...analyticsData.monthlyAppointments.map(m => m.value), ...analyticsData.prevMonthlyAppointments.map(m => m.value), 1);
                    return analyticsData.monthlyAppointments.map((item, index) => {
                      const prevValue = analyticsData.prevMonthlyAppointments[index]?.value || 0;
                      return (
                        <div key={index} className="chart-bar">
                          <div className="bar-value">{item.value}</div>
                          <div className="bar-area">
                            <div className="bar-fill appointments prev-year" style={{ height: `${(prevValue / max) * 100}%` }} title={`${selectedYear - 1}: ${prevValue}`}></div>
                            <div className="bar-fill appointments" style={{ height: `${(item.value / max) * 100}%` }}></div>
                          </div>
                          <div className="bar-label">{item.month}</div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            </div>
            <div className="chart-card">
              <h4>{t('analytics.reservationsPerMonth')}</h4>
              <div className="chart-legend">
                <div className="chart-legend-item"><div className="chart-legend-swatch bar-fill prev-year"></div><span>{selectedYear - 1}</span></div>
                <div className="chart-legend-item"><div className="chart-legend-swatch bar-fill"></div><span>{selectedYear}</span></div>
              </div>
              <div className="chart-placeholder">
                <div className="chart-bars">
                  {(() => {
                    const max = Math.max(...analyticsData.monthlyReservations.map(m => m.value), ...analyticsData.prevMonthlyReservations.map(m => m.value), 1);
                    return analyticsData.monthlyReservations.map((item, index) => {
                      const prevValue = analyticsData.prevMonthlyReservations[index]?.value || 0;
                      return (
                        <div key={index} className="chart-bar">
                          <div className="bar-value">{item.value}</div>
                          <div className="bar-area">
                            <div className="bar-fill prev-year" style={{ height: `${(prevValue / max) * 100}%` }} title={`${selectedYear - 1}: ${prevValue}`}></div>
                            <div className="bar-fill" style={{ height: `${(item.value / max) * 100}%` }}></div>
                          </div>
                          <div className="bar-label">{item.month}</div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            </div>
            <div className="chart-card">
              <h4>{t('analytics.successRatePerMonth')}</h4>
              <div className="chart-legend">
                <div className="chart-legend-item"><div className="chart-legend-swatch bar-fill success-rate prev-year"></div><span>{selectedYear - 1}</span></div>
                <div className="chart-legend-item"><div className="chart-legend-swatch bar-fill success-rate"></div><span>{selectedYear}</span></div>
              </div>
              <div className="chart-placeholder">
                <div className="chart-bars">
                  {(() => {
                    const max = Math.max(...analyticsData.monthlySuccessRate.map(m => m.value), ...analyticsData.prevMonthlySuccessRate.map(m => m.value), 1);
                    return analyticsData.monthlySuccessRate.map((item, index) => {
                      const prevValue = analyticsData.prevMonthlySuccessRate[index]?.value || 0;
                      return (
                        <div key={index} className="chart-bar">
                          <div className="bar-value">{item.value}%</div>
                          <div className="bar-area">
                            <div className="bar-fill success-rate prev-year" style={{ height: `${(prevValue / max) * 100}%` }} title={`${selectedYear - 1}: ${prevValue}%`}></div>
                            <div className="bar-fill success-rate" style={{ height: `${(item.value / max) * 100}%` }} title={`${item.reservations} ${t('analytics.reservationsFrom')} ${item.appointments} ${t('analytics.appointmentsText')}`}></div>
                          </div>
                          <div className="bar-label">{item.month}</div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            </div>
            <div className="chart-card">
              <h4>{t('analytics.reservationPricePerMonth')} ({currencySymbol})</h4>
              <div className="chart-legend">
                <div className="chart-legend-item"><div className="chart-legend-swatch bar-fill reservation-price prev-year"></div><span>{selectedYear - 1}</span></div>
                <div className="chart-legend-item"><div className="chart-legend-swatch bar-fill reservation-price"></div><span>{selectedYear}</span></div>
              </div>
              <div className="chart-placeholder">
                <div className="chart-bars">
                  {(() => {
                    const max = Math.max(...analyticsData.monthlyAverageReservationPrice.map(m => m.value), ...analyticsData.prevMonthlyAverageReservationPrice.map(m => m.value), 1);
                    return analyticsData.monthlyAverageReservationPrice.map((item, index) => {
                      const prevValue = analyticsData.prevMonthlyAverageReservationPrice[index]?.value || 0;
                      return (
                        <div key={index} className="chart-bar">
                          <div className="bar-value">{item.value > 0 ? item.value.toLocaleString() : '—'}</div>
                          <div className="bar-area">
                            <div className="bar-fill reservation-price prev-year" style={{ height: `${(prevValue / max) * 100}%` }} title={`${selectedYear - 1}: ${prevValue.toLocaleString()}`}></div>
                            <div className="bar-fill reservation-price" style={{ height: `${(item.value / max) * 100}%` }}></div>
                          </div>
                          <div className="bar-label">{item.month}</div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Popular Dresses */}
        <div className="analytics-section">
          <div className="section-header">
          <h3>{t('analytics.popularDresses')}</h3>
            <div className="popular-dresses-controls">
              <select
                className="popular-dresses-sort-select"
                value={popularDressesSortBy}
                onChange={(e) => setPopularDressesSortBy(e.target.value)}
                title={t('analytics.sortBy')}
                aria-label={t('analytics.sortBy')}
              >
                <option value="reservations">{t('analytics.sortByReservations')}</option>
                <option value="revenue">{t('analytics.sortByRevenue')}</option>
                <option value="interests">{t('analytics.sortByInterests')}</option>
              </select>
              <div className="year-selector">
                <button 
                  className={`year-nav-btn ${popularDressesYear === 'current' ? 'active' : ''}`}
                  onClick={() => setPopularDressesYear('current')}
                  title={selectedYear.toString()}
                >
                  {selectedYear}
                </button>
                <button 
                  className={`year-nav-btn ${popularDressesYear === 'total' ? 'active' : ''}`}
                  onClick={() => setPopularDressesYear('total')}
                  title={t('analytics.total')}
                >
                  {t('analytics.total')}
                </button>
              </div>
            </div>
          </div>
          <div className="popular-dresses">
            {analyticsData.popularDresses.map((dress, index) => (
              <div key={dress.id} className="dress-item">
                <div className="dress-rank">#{index + 1}</div>
                <div className="dress-info">
                  <DressDisplay 
                    dress={dress}
                    onImageClick={handleImageClick}
                    imageClassName="analytics-dress-image"
                    showBrand={true}
                  />
                </div>
                <div className="dress-stats">
                  <div className="stat">{dress.reservations} {t('analytics.reservationsCount')}</div>
                  <div className="stat">{formatPrice(dress.revenue)}</div>
                  <div className="stat">{dress.interestedCount ?? 0} {t('analytics.interestedCount')}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Popular Brands */}
        <div className="analytics-section">
          <div className="section-header">
            <h3>{t('analytics.popularBrands')}</h3>
            <div className="year-selector">
              <button 
                className={`year-nav-btn ${popularBrandsYear === 'current' ? 'active' : ''}`}
                onClick={() => setPopularBrandsYear('current')}
                title={selectedYear.toString()}
              >
                {selectedYear}
              </button>
              <button 
                className={`year-nav-btn ${popularBrandsYear === 'total' ? 'active' : ''}`}
                onClick={() => setPopularBrandsYear('total')}
                title={t('analytics.total')}
              >
                {t('analytics.total')}
              </button>
            </div>
          </div>
          <div className="popular-brands">
            {analyticsData.popularBrands.map((brand, index) => (
              <div key={brand.id} className="brand-item">
                <div className="brand-rank">#{index + 1}</div>
                <div className="brand-info">
                  <div className="brand-image">
                    {brand.image ? (
                      <img 
                        src={getThumbnailUrl(brand.image)} 
                        alt={brand.name}
                        className="analytics-brand-image"
                      />
                    ) : (
                      <div className="brand-placeholder">
                        <span>🏷️</span>
                      </div>
                    )}
                  </div>
                  <div className="brand-details">
                    <div className="brand-name">{brand.name}</div>
                    <div className="brand-country">{brand.country}</div>
                  </div>
                </div>
                <div className="brand-stats">
                  <div className="stats-column">
                    <div className="stat">{formatPrice(brand.revenue)}</div>
                    <div className="stat">{brand.reservations} {t('analytics.reservationsCount')}</div>
                  </div>
                  <div className="stats-column">
                    <div className="stat">{brand.dresses} {t('analytics.dressesCount')}</div>
                    <div className="stat">{brand.reservationsPerDress} {t('analytics.perDress')}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Popular Referral Sources */}
        <div className="analytics-section">
          <div className="section-header">
            <h3>{t('analytics.popularReferralSources')}</h3>
            <div className="year-selector">
              <button 
                className={`year-nav-btn ${popularReferralSourcesYear === 'current' ? 'active' : ''}`}
                onClick={() => setPopularReferralSourcesYear('current')}
                title={selectedYear.toString()}
              >
                {selectedYear}
              </button>
              <button 
                className={`year-nav-btn ${popularReferralSourcesYear === 'total' ? 'active' : ''}`}
                onClick={() => setPopularReferralSourcesYear('total')}
                title={t('analytics.total')}
              >
                {t('analytics.total')}
              </button>
            </div>
          </div>
          <div className="popular-sources">
            {analyticsData.popularReferralSources.length === 0 ? (
              <div className="no-data-message">
                {t('analytics.noReferralSourcesData')}
              </div>
            ) : (
              analyticsData.popularReferralSources.map((source, index) => (
                <div key={source.name} className="source-item">
                  <div className="source-rank">#{index + 1}</div>
                  <div className="source-info">
                    <div className="source-icon">📊</div>
                    <div className="source-details">
                      <div className="source-name">{source.name}</div>
                    </div>
                  </div>
                  <div className="source-stats">
                    <div className="stat">{formatPrice(source.revenue)}</div>
                    <div className="stat">{source.reservations} {t('analytics.reservationsCount')}</div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Bride Cities */}
        <div className="analytics-section">
          <div className="section-header">
            <h3>{t('analytics.brideCities')}</h3>
            <div className="year-selector">
              <button 
                className={`year-nav-btn ${brideCitiesYear === 'current' ? 'active' : ''}`}
                onClick={() => setBrideCitiesYear('current')}
                title={selectedYear.toString()}
              >
                {selectedYear}
              </button>
              <button 
                className={`year-nav-btn ${brideCitiesYear === 'total' ? 'active' : ''}`}
                onClick={() => setBrideCitiesYear('total')}
                title={t('analytics.total')}
              >
                {t('analytics.total')}
              </button>
            </div>
          </div>
          <div className="popular-sources">
            {analyticsData.brideCities.length === 0 ? (
              <div className="no-data-message">
                {t('analytics.noBrideCitiesData')}
              </div>
            ) : (
              analyticsData.brideCities.map((city, index) => (
                <div key={`${city.name}-${index}`} className="source-item">
                  <div className="source-rank">#{index + 1}</div>
                  <div className="source-info">
                    <div className="source-icon">🏙️</div>
                    <div className="source-details">
                      <div className="source-name">{city.name}</div>
                    </div>
                  </div>
                  <div className="source-stats">
                    <div className="stat">{city.count} {t('analytics.reservationsCount')}</div>
                    <div className="stat">{city.percentage}%</div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Bride Age */}
        <div className="analytics-section">
          <div className="section-header">
            <h3>{t('analytics.brideAgeAtReservation')}</h3>
            <div className="year-selector">
              <button 
                className={`year-nav-btn ${brideAgeYear === 'current' ? 'active' : ''}`}
                onClick={() => setBrideAgeYear('current')}
                title={selectedYear.toString()}
              >
                {selectedYear}
              </button>
              <button 
                className={`year-nav-btn ${brideAgeYear === 'total' ? 'active' : ''}`}
                onClick={() => setBrideAgeYear('total')}
                title={t('analytics.total')}
              >
                {t('analytics.total')}
              </button>
            </div>
          </div>
          <p className="analytics-caption">
            {t('analytics.basedOnReservations', 'Based on {count} reservations', { count: analyticsData.brideAgeHistogram.totalDataPoints })}
          </p>
          <div className="bride-age-stats">
            <div className="bride-age-stat">
              <span className="bride-age-stat-label">{t('analytics.brideAgeMedian', 'Median')}:</span>
              <span className="bride-age-stat-value">
                {analyticsData.brideAgeHistogram.medianAge != null
                  ? t('analytics.brideAgeYears', '{age} years', { age: analyticsData.brideAgeHistogram.medianAge })
                  : t('analytics.brideAgeNoData', '—')}
              </span>
            </div>
            <div className="bride-age-stat">
              <span className="bride-age-stat-label">{t('analytics.brideAgeAverage', 'Average')}:</span>
              <span className="bride-age-stat-value">
                {analyticsData.brideAgeHistogram.averageAge != null
                  ? t('analytics.brideAgeYears', '{age} years', { age: analyticsData.brideAgeHistogram.averageAge })
                  : t('analytics.brideAgeNoData', '—')}
              </span>
            </div>
            <div className="bride-age-stat">
              <span className="bride-age-stat-label">{t('analytics.brideAgeRange90', '90%')}:</span>
              <span className="bride-age-stat-value">
                {analyticsData.brideAgeHistogram.range90 != null
                  ? t('analytics.brideAgeYearsOld', '{min}–{max} years old', {
                      min: analyticsData.brideAgeHistogram.range90[0],
                      max: analyticsData.brideAgeHistogram.range90[1]
                    })
                  : t('analytics.brideAgeNoData', '—')}
              </span>
            </div>
            <div className="bride-age-stat">
              <span className="bride-age-stat-label">{t('analytics.brideAgeRange95', '95%')}:</span>
              <span className="bride-age-stat-value">
                {analyticsData.brideAgeHistogram.range95 != null
                  ? t('analytics.brideAgeYearsOld', '{min}–{max} years old', {
                      min: analyticsData.brideAgeHistogram.range95[0],
                      max: analyticsData.brideAgeHistogram.range95[1]
                    })
                  : t('analytics.brideAgeNoData', '—')}
              </span>
            </div>
          </div>
          <div className="chart-card">
            {/* <h4>{t('analytics.brideAgeAtReservation')}</h4> */}
            <div className="chart-placeholder">
              <div className="chart-bars">
                {analyticsData.brideAgeHistogram.data.map((item, index) => {
                  const values = analyticsData.brideAgeHistogram.data.map(d => d.value);
                  const max = Math.max(1, ...values);
                  return (
                    <div key={index} className="chart-bar">
                      <div className="bar-value">{item.value}</div>
                      <div 
                        className="bar-fill bride-age" 
                        style={{ height: `${(item.value / max) * 100}%` }}
                      ></div>
                      <div className="bar-label">{item.age}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Employee conversion (assigned appointments → linked reservation) */}
        <div className="analytics-section">
          <h3>{t('analytics.employeeConversionTitle')}</h3>
          <p className="analytics-section-description">
            {t('analytics.employeeConversionDescription', 'Share of assigned appointments that led to a linked reservation in {year}.', { year: selectedYear })}
          </p>
          {analyticsData.employeeConversionStats.length === 0 ? (
            <p className="analytics-empty-message">{t('analytics.noEmployeeConversionData')}</p>
          ) : (
            <div className="employee-conversion-grid">
              {analyticsData.employeeConversionStats.map((employee) => (
                <div key={employee.employeeId} className="chart-card employee-conversion-card">
                  <div className="employee-conversion-card-header">
                    <div>
                      <span className="employee-conversion-name">{employee.name}</span>
                      <span className="employee-conversion-role">
                        {employee.role === 'admin'
                          ? t('analytics.roleAdmin')
                          : t('analytics.roleMember')}
                      </span>
                    </div>
                    <span className="employee-conversion-year-summary">
                      {t('analytics.employeeConversionYearSummary', '{rate}% ({linked}/{total})', {
                        rate: employee.yearTotal.rate,
                        linked: employee.yearTotal.linked,
                        total: employee.yearTotal.appointments
                      })}
                    </span>
                  </div>
                  <h4>{t('analytics.employeeConversionPerMonth')}</h4>
                  <div className="chart-legend">
                    <div className="chart-legend-item">
                      <div className="chart-legend-swatch bar-fill success-rate"></div>
                      <span>{selectedYear}</span>
                    </div>
                  </div>
                  <div className="chart-placeholder">
                    <div className="chart-bars">
                      {employee.monthly.map((item, index) => (
                        <div key={index} className="chart-bar">
                          <div className="bar-value bar-value-stacked">
                            <span>{item.value}%</span>
                            <span className="bar-value-fraction">
                              {item.appointments > 0 ? `${item.linked}/${item.appointments}` : '—'}
                            </span>
                          </div>
                          <div className="bar-area">
                            <div
                              className="bar-fill success-rate"
                              style={{ height: `${item.value}%` }}
                              title={t('analytics.employeeConversionBarTooltip', '{linked}/{total}', {
                                linked: item.linked,
                                total: item.appointments
                              })}
                            ></div>
                          </div>
                          <div className="bar-label">{item.month}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Activity */}
        <div className="analytics-section">
          <h3>{t('analytics.recentActivity')}</h3>
          <div className="activity-list">
            {analyticsData.recentActivity.map((activity, index) => (
              <div key={index} className="activity-item">
                <div className={`activity-icon ${activity.type}`}>
                  {activity.type === 'reservation' ? '📋' : '📅'}
                </div>
                <div className="activity-content">
                  <div className="activity-title">{activity.title}</div>
                  <div className="activity-date">
                    {activity.date.toLocaleDateString()} {t('analytics.at')} {activity.date.toLocaleTimeString()}
                  </div>
                </div>
                {activity.amount > 0 && (
                  <div className="activity-amount">{formatPrice(activity.amount)}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Image Viewer Modal */}
      {imageViewerOpen && (
        <ImageViewerModal
          images={selectedDressImages}
          isOpen={imageViewerOpen}
          onClose={handleCloseImageViewer}
        />
      )}
    </div>
  );
};

export default ManageAnalytics;
